import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {ApiLayerConfiguration} from "../config/api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";

import {ProcessSource, ProcessSourceProps} from "../models/process-source";
import EsriFeatureLayer from "@arcgis/core/layers/FeatureLayer";
import EsriFeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";
import {MapState} from "../core/map-state";
import {MainProcessSourceFilter} from "../filters/main-process-source-filter";

export interface ProcessSourcesStoreProps {
    layerConfig: ApiLayerConfiguration;
    mapStore: MapState;
}

export class ProcessSourcesStore extends AbstractStore {

    mapStore: MapState;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, ProcessSource>();

    layerView: EsriFeatureLayerView;

    constructor(props: ProcessSourcesStoreProps) {

        super({ layerConfig: props.layerConfig });
        this.mapStore = props.mapStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            filters: observable,
            sort: observable,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): ProcessSource[] {
        // apply filters
        let filtered = [...this.items.values()];

        if (this.filters && this.filters.length > 0) {
            filtered = MainProcessSourceFilter.apply(filtered, this.filters);
        }

        let searchedProcessSources: ProcessSource[] | undefined;
        // if (searchTermLower && searchTermLower.length > 0) {
        //     // searchedProcessSources = filtered.filter((c) => {
        //     //     return c.artId?.includes(searchTermLower);
        //     // });
        // }
        // else {
            // no search filter
            searchedProcessSources = filtered;
        // }

        // sort
        searchedProcessSources.sort(ProcessSource.sortByName);
        return [ ...searchedProcessSources];
    }

    zoomToItem(guid: string): void {
        if (!guid) {
            return;
        }
        this.loadItem(guid).then((item: ProcessSource) => {
            this.mapStore?.view?.goTo(item.feature)
                .catch((error) => {
                    if (error.name !== "view:goto-interrupted") {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                });
        });
    }

    loadItem(guid: string | undefined): Promise<ProcessSource | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!guid) {
                    reject("No GUID provided.");
                    return;
                }

                this.queryFeatures([guid]).then((processSources: ProcessSource[]) => {
                    if (processSources[0] && processSources[0].guid === guid) {
                        const processSource = processSources[0];
                        const loadedProcessSource = this.items.get(processSource.guid);
                        if (loadedProcessSource) {
                            // return process source as long as merging process source is running.
                            // process source can be displayed with indicator that syncing is in progress
                            // TODO: merge process source with already loaded process source
                        }
                        this.addItem(processSource);
                        this.setSelectedItem(processSource);
                        this.setDataStatus("loaded");
                        resolve(processSource);
                    } else {
                        this.setDataStatus("error");
                        reject("Process source not found.");
                        throw new Error("Process source not found");
                    }
                });
            });
        });
    }

    /**
     * Request process sources from server and store in local store
     * @param guids (Optional) IDs of process sources
     */
    loadItems(guids?: string[]): Promise<ProcessSource[]> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available
                this.queryFeatures(guids).then((processSources: ProcessSource[]) => {
                    if (processSources) {
                        const map = new Map<string, ProcessSource>();
                        processSources.forEach((p: ProcessSource) => {
                            map.set(String(p.guid), p);
                        });
                        this.addItem(map);
                        this.setDataStatus("loaded");

                        resolve(processSources);
                    } else {
                        this.setDataStatus("error");
                        reject("Error requesting process sources.");
                        throw new Error("Error requesting process sources");
                    }
                });
            });
        });
    }

    queryFeatures(guids?: string[], unique?: true): Promise<ProcessSource[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                if (unique) {
                    queryParams.returnDistinctValues = true;
                }

                // load all features if no project ID is specified
                if (guids !== undefined) {
                    // Add to the layer's current definitionExpression
                    // remove undefined or null values
                    const validIds = guids.filter((id) => id !== null && id !== undefined);
                    if (validIds.length > 0) {
                        // use prozessquelle here, as it is used everywhere as primary key
                        queryParams.where += ` AND prozessquelle IN ('${validIds.join("','")}')`;
                    }
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const processSources = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(processSources);
                    }
                    reject("No features found.");
                }, (error) => {
                    throw new Error(`Error querying process sources + ${error}`);
                });
            });
        });
    }

    queryFeaturesFromDb(geometry?: __esri.Geometry,
                        timeExtent?: __esri.TimeExtent,
                        spatialRelationship?: "intersects"
                            | "contains"
                            | "crosses"
                            | "disjoint"
                            | "envelope-intersects"
                            | "index-intersects"
                            | "overlaps"
                            | "touches"
                            | "within"
                            | "relation"): Promise<ProcessSource[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                const queryParams = this.layer.createQuery();
                if (geometry) {
                    queryParams.geometry = geometry;
                }
                if (timeExtent) {
                    queryParams.timeExtent = timeExtent;
                }
                if (spatialRelationship) {
                    queryParams.spatialRelationship = spatialRelationship;
                }

                // query the layer view with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const processSources = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(processSources);
                    } else {
                        reject("No features found.");
                    }
                }, (error) => {
                    throw new Error(`Error querying process sources + ${error}`);
                });
            });
        });
    }

    /**
     * Override loadLayer to also load view layer
     * @param layerConfig
     * @protected
     */
    protected loadLayer(layerConfig: ApiLayerConfiguration): Promise<EsriFeatureLayer> {
        return new Promise((resolve, reject) => {
            try {
                // wait for map view > find the instance of this layer loaded to the map
                // the perimeter layer is loaded twice. Once for the API, once to show in the map
                autorun((reaction: any) => {
                    if (!this.mapStore?.view || !this.mapStore?.map) {
                        return;
                    }
                    reaction.dispose(); // stop autorun

                    // get layer and layerView from map
                    this.layer = this.mapStore.map.layers.flatten((item: __esri.Layer) => {
                        return (item as __esri.GroupLayer)?.layers;
                    }).find((layer: __esri.FeatureLayer) => {
                        return layer.portalItem?.id === layerConfig.portalItem.id &&
                            layer.layerId === layerConfig.layerId;
                    }) as __esri.FeatureLayer;
                    if (!this.layer) {
                        reject(layerConfig.objectType);
                        return;
                    }
                    this.mapStore.view.whenLayerView(this.layer).then((featureLayerView: EsriFeatureLayerView) => {
                        this.layerView = featureLayerView;
                        resolve(this.layer);
                    });
                });
            } catch (errors) {
                console.error(errors);
                reject(layerConfig.objectType);
            }
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): ProcessSource[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const processSources: ProcessSource[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, ProcessSource);
            const processSource = new ProcessSource(json as ProcessSourceProps);
            processSource.feature = feature;

            processSources.push(processSource);
        });
        return processSources;
    }

}
